/**
 * Billing settings — the merchant's billing home on the SaaS (App Store
 * 1.2.3: change plan and interval without contacting support).
 *
 * Layout, centred: the news first — a switch waiting for the merchant's
 * approval, a switch that was declined or expired, an Enterprise agreement
 * still billed on the old plan — then the paywall's card (PlanCard +
 * PlanOffer): the store's plan, how the band is set, what every plan
 * includes, and the action; then the charge terms. Shopify returns a
 * merchant here after a switch (`RETURN_SCREENS['plans']`), approved or
 * declined, so the outcome is the first thing they read. While the price
 * loads, fails to load or cannot be read yet, the card is the paywall's
 * state card for it (LoadingCard, RetryCard, NotReadyCard).
 *
 * The one difference from the paywall: a merchant
 * with an approved plan gets no Monthly / Annual toggle — the card shows the
 * interval they pay, and the button switches it. A merchant with nothing
 * approved (`trial`, `grace`) picks on the toggle, as on the paywall.
 *
 * Only the store's own plan is shown: the band
 * follows revenue, so the merchant cannot pick another one. Every price
 * stays one click away in the terms' link to the public pricing page.
 *
 * The status comes from the layout's billing read (`BillingStatusContext`),
 * which already let this store into the app; the page reads the pricing
 * itself. Nothing about the free period is stated here — a merchant inside
 * it simply sees Subscribe.
 */

import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Link,
  Stack,
  Typography,
} from '@mui/material';

import { useAuth } from '../contexts/AuthContext';
import { useBillingStatusContext } from '../contexts/BillingStatusContext';
import { useFormatters } from '../hooks/useFormatters';
import { apiJson, translateRequestError } from '../queries/api';
import { showToast } from '../components/common/appToastBus';
import { useApiMutation } from '../queries/useApiMutation';
import { billingPricing as billingPricingKey } from '../queries/keys';
import { WITHDRAW_STALE_CODES, merchantProposal } from '../billing/planChange';
import { canChangePlan, quotedPendingMove } from '../billing/planState';
import { BillingNotices, PlanAction } from '../components/billing/BillingControls';
import {
  LoadingCard, NotReadyCard, PlanCard, PlanOffer, RetryCard,
} from '../components/billing/PlanCard';
import { hasUsablePrices } from '../components/billing/pricingPayload';
import {
  PRICING_NOT_READY_STATUS, SUBSCRIBE_ERROR_KEYS, SWITCH_ERROR_KEYS, planLabel, tierExplainerKey,
} from '../components/billing/planCopy';
import { Clock, Sparkle, WarningCircle } from '../icons';
import { FAILURE_STATE, failureState } from '../utils/syncGate';
import '../styles/pages.css';

const PRICING_PAGE_URL = 'https://manexys.com/#pricing';
const BILLING_INTERVALS = ['monthly', 'annual'];

/** The page frame: one centred column, the way the paywall sits. */
function Page({ children }) {
  return (
    <Box className="page-container" sx={{ alignItems: 'center' }}>
      <Stack spacing={2.5} className="plan-column">{children}</Stack>
    </Box>
  );
}

export default function PlansPage() {
  const { t } = useTranslation();
  const { formatNumber } = useFormatters();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id ?? null;
  const billing = useBillingStatusContext();
  // The card's toggle, shown only while nothing is approved.
  const [isAnnual, setIsAnnual] = useState(false);

  const pricingQuery = useQuery({
    queryKey: billingPricingKey.all(storeId),
    queryFn: () => apiJson.get('/api/billing/pricing/'),
    enabled: Boolean(billing) && Boolean(storeId),
    // The one screen whose job is stating the price: never a stale one.
    staleTime: 0,
  });

  // Every push hands the browser to Shopify's approval page; there is no
  // success state to render on this page.
  const toShopify = (data) => {
    if (data?.confirmation_url) {
      window.location.assign(data.confirmation_url);
      return;
    }
    // A 200 without the approval page is a contract break, not a refusal the
    // merchant can do anything about: the same tail a refused request gets.
    console.error('Plans page: no confirmation_url in the push response');
    showToast(`${t('billing.errors.noConfirmationUrl')} ${t('requestFailed.refused')}`, 'error');
  };

  // Billing is read through BillingStatusContext, not the query cache: none of
  // these writes names a kind of store data.
  const switchMutation = useApiMutation({
    mutationFn: (interval) => apiJson.post('/api/billing/switch/', { billing_interval: interval }),
    changes: [],
    onSuccess: toShopify,
    onError: (err) => showToast(translateRequestError(
      err, t, 'billing.plans.change.failed', { codeKeys: SWITCH_ERROR_KEYS },
    ), 'error'),
  });
  const subscribeMutation = useApiMutation({
    mutationFn: (interval) => apiJson.post(
      '/api/billing/subscribe/', { billing_interval: interval, return_to: 'plans' },
    ),
    changes: [],
    onSuccess: toShopify,
    onError: (err) => showToast(translateRequestError(
      err, t, 'billing.errors.subscribeFailed', { codeKeys: SUBSCRIBE_ERROR_KEYS },
    ), 'error'),
  });
  // Withdrawing asks Shopify nothing, so the page stays and the status is
  // re-read: the notice is driven by it.
  const withdrawMutation = useApiMutation({
    mutationFn: () => apiJson.post('/api/billing/switch/withdraw/', {}),
    changes: [],
    onSuccess: () => billing.refresh(),
    onError: (err) => showToast(translateRequestError(
      err, t, 'billing.planChange.pending.withdrawFailed', {
        codeKeys: Object.fromEntries(
          WITHDRAW_STALE_CODES.map((code) => [code, 'billing.planChange.pending.withdrawStale']),
        ),
      },
    ), 'error'),
  });

  // A 200 with no numbers to quote is a broken payload — only an unquoted
  // Enterprise band has none, and it says so (`contact_sales`). Logged once
  // per payload, and the page shows the load failure below.
  const pricesBroken = pricingQuery.isSuccess
    && !pricingQuery.data.prices?.contact_sales
    && !hasUsablePrices(pricingQuery.data.prices);
  useEffect(() => {
    if (pricesBroken) console.error('Plans page: pricing payload carried no usable prices');
  }, [pricesBroken]);
  // An approved plan always has a cadence; a blank one (the column allows
  // it) would read as "this is your current plan" on whichever interval is
  // selected. A broken status, not a default.
  const intervalBroken = Boolean(billing) && canChangePlan(billing.status.status)
    && !BILLING_INTERVALS.includes(billing.status.billing_interval);
  useEffect(() => {
    if (intervalBroken) console.error('Plans page: approved plan carried no billing interval');
  }, [intervalBroken]);

  // Stays true through the redirect: the buttons must not re-arm while the
  // browser is leaving for Shopify.
  const isRedirecting = switchMutation.isPending || switchMutation.isSuccess
    || subscribeMutation.isPending || subscribeMutation.isSuccess;

  if (!billing) {
    return (
      <Page>
        <div className="plan-card">
          <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>{t('billing.plans.notApplicable')}</Typography>
        </div>
      </Page>
    );
  }

  if (pricingQuery.isPending) {
    return (
      <Page>
        <LoadingCard />
      </Page>
    );
  }

  const pricing = pricingQuery.data;
  if (pricingQuery.isError || pricesBroken || intervalBroken) {
    // The structured refusal, not any 503: a gateway 503 carries no body.
    const notReady = pricingQuery.error?.status === PRICING_NOT_READY_STATUS
      && pricingQuery.error.code === 'pricing_not_ready';
    // The price waits on an import Shopify refused: say what that asks.
    const refused = notReady ? pricingQuery.error.body.detail.import_refused : null;
    if (refused !== null) {
      const reconnect = failureState(refused) === FAILURE_STATE.RECONNECT;
      return (
        <Page>
          <PlanCard
            // Reconnect asks something of the merchant; waiting asks nothing.
            icon={reconnect ? WarningCircle : Clock}
            heading={reconnect ? t('billing.importRefused.reconnectTitle') : t('billing.importRefused.waitingTitle')}
            subtitle={reconnect ? t('billing.importRefused.reconnectBody') : t('billing.importRefused.waitingBody')}
          >
            <div className="plan-card-cta">
              {reconnect ? (
                <Button variant="contained" href={selectedStore.shopify_sign_in_url}>
                  {t('billing.importRefused.reconnectCta')}
                </Button>
              ) : (
                <Button variant="contained" onClick={() => pricingQuery.refetch()}>{t('billing.retry')}</Button>
              )}
            </div>
          </PlanCard>
        </Page>
      );
    }
    return (
      <Page>
        {notReady ? (
          <NotReadyCard onRetry={() => pricingQuery.refetch()} />
        ) : (
          <RetryCard
            title={t('billing.loadFailedTitle')}
            body={t('billing.loadFailedBody')}
            onRetry={() => pricingQuery.refetch()}
          />
        )}
      </Page>
    );
  }

  const status = billing.status;
  const pendingChange = merchantProposal(status.pending_tier_change);
  const currentPlanLabel = planLabel(
    t, formatNumber,
    status.tier_display ? status.tier_display : t('billing.planChange.currentPlanFallback'),
    status.billing_interval, status.price,
  );
  const { prices } = pricing;
  // A store with an approved plan whose revenue moved it to another band:
  // the card shows the band it can move to, so what it pays today is stated
  // too. A quoted Enterprise move is the notice's (`quotedPendingMove`).
  const hasApprovedPlan = canChangePlan(status.status);
  const bandMoved = hasApprovedPlan && !pricing.enterprise_offer && status.tier !== prices.tier;

  return (
    <Page>
      <BillingNotices
        pendingChange={pendingChange}
        unapprovedChange={status.unapproved_change}
        quotedMove={quotedPendingMove({
          status: status.status,
          tier: status.tier,
          planName: status.tier_display,
          paidUntil: status.paid_until,
        }, pricing)}
        currentPlanLabel={currentPlanLabel}
        renewalDate={status.paid_until}
        onWithdraw={() => withdrawMutation.mutate()}
        isWithdrawing={withdrawMutation.isPending}
        isRedirecting={isRedirecting}
      />

      <PlanCard
        icon={Sparkle}
        heading={bandMoved ? t('billing.plans.newPlanHeading') : t('billing.plans.yourPlan')}
        subtitle={t(tierExplainerKey(pricing))}
      >
        <PlanOffer
          prices={prices}
          isAnnual={hasApprovedPlan ? status.billing_interval === 'annual' : isAnnual}
          onIntervalChange={hasApprovedPlan ? undefined : setIsAnnual}
          note={bandMoved ? t('billing.plans.currentlyBilled', { plan: currentPlanLabel }) : null}
        />
        <Box className="plan-card-cta" sx={{ '&:empty': { display: 'none' } }}>
          <PlanAction
            status={status.status}
            currentTier={status.tier}
            currentInterval={status.billing_interval}
            prices={prices}
            interval={isAnnual ? 'annual' : 'monthly'}
            parked={Boolean(pendingChange)}
            onSwitch={(value) => switchMutation.mutate(value)}
            onSubscribe={(value) => subscribeMutation.mutate(value)}
            isRedirecting={isRedirecting}
          />
        </Box>
      </PlanCard>

      {/* Currency, cadence, who bills, and the full public price list. */}
      <Typography variant="body2" color="text.secondary" align="center">
        <Trans
          i18nKey="billing.plans.billingNote"
          components={{
            pricing: <Link href={PRICING_PAGE_URL} target="_blank" rel="noopener noreferrer" />,
          }}
        />
      </Typography>
    </Page>
  );
}
